import React, { useEffect, useState } from 'react';
import {
    DetailsList,
    IconButton,
    IContextualMenuItem,
    IContextualMenuProps,
    Selection,
    SelectionMode,
    IColumn,
    MessageBarType,
    TooltipOverflowMode,
    IStackStyles,
    StackItem,
    IDropdownOption,
    IDropdownStyles, ActionButton, Stack
} from '@fluentui/react';
import { useBoolean } from "@fluentui/react-hooks";
import { useAuth } from "react-oidc-context";
import styles from './CAEndUserAssignment.module.css';
import CAEndUserAssignmentModal from './CAEndUserAssignmentModal';
import CAEndUserAssignmentDeleteModal from './CAEndUserAssignmentDeleteModal';
import { displayMessageBar } from '../../LayoutWithAuth/LayoutWithAuth';
import { TooltipHost } from "@fluentui/react/lib/Tooltip";
import EiopaFnPIcons from "../../EiopaFnPIcons/EiopaFnPIcons";
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import { Delete20Filled, FluentIconsProps } from "@fluentui/react-icons";
import QuickSearch from '../QuickSearch/QuickSearch';
import Pagination from "../../Pagination/Pagination";
import strings from "../../../loc/strings";
import {
    fetchCAEndUserAssignments,
    fetchAvailableCAEndUsers,
    fetchActiveLegalBases,
    CAEndUserAssignmentSummary,
    EUSectoralLegislatureBasis
} from "../../../services/AdminServices/CAEndUserAssignmentService";
import {PERSON_CONTACT_TYPE} from "../../../constants/EiopaFnPConstants.ts";

initializeIcons();

const options: IDropdownOption[] = [
    { key: 'userEmail', text: 'User email' },
    { key: 'assignedLegalBases', text: 'EU sectoral legislative basis' },
];

const stackStyles: Partial<IStackStyles> = {
    root: {
        margin: "0px auto",
    },
};
const stackStylesNoResults: Partial<IStackStyles> = {
    root: {
        paddingTop: "16px",
    },
};
interface IPagination {
    columnsState: IColumn[];
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
}

const customDropdownStyles: Partial<IDropdownStyles> = {
    dropdown: { width: 400 }
};

const customTextFieldStyles = {
    fieldGroup: {
        width: 400
    },
};

function CAEndUserAssignment() {
    const auth = useAuth();
    const [deleteUserEmail, setDeleteUserEmail] = useState<string>('');
    const [isSortedDescendings, { toggle: toggleIsSortedDescendings }] = useBoolean(true);
    
    // State for assignments and supporting data
    const [items, setItems] = useState<CAEndUserAssignmentSummary[]>([]);
    const [itemsOrg, setItemsOrg] = useState<CAEndUserAssignmentSummary[]>([]);
    const [totalItems, setTotalItems] = useState<number>();
    const [availableUsers, setAvailableUsers] = useState<string[]>([]);
    const [activeLegalBases, setActiveLegalBases] = useState<EUSectoralLegislatureBasis[]>([]);
    
    // Modal states
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [isAddNew, setIsAddNew] = useState<boolean>(false);
    const [deleteDialogHide, setDeleteDialogHide] = useState<boolean>(true);
    const [editItem, setEditItem] = useState<CAEndUserAssignmentSummary | null>(null);
    const [deleteItem, setDeleteItem] = useState<CAEndUserAssignmentSummary | null>(null);

    // Columns definition
    const assignmentColumns: IColumn[] = [
        { 
            key: 'column1', 
            name: 'User email',
            fieldName: 'userEmail', 
            minWidth: 300, 
            maxWidth: 400, 
            isResizable: true,
            isSorted: true,
            isSortedDescending: isSortedDescendings,
            sortAscendingAriaLabel: 'Sorted A to Z',
            sortDescendingAriaLabel: 'Sorted Z to A',
            onColumnClick: _onColumnClick,
            onRenderHeader: () => (
                <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={'User email'}>{'User email'}</TooltipHost>
            ),
        },
        {
            key: 'column2', 
            name: 'EU sectoral legislative bases',
            fieldName: 'assignedLegalBases', 
            minWidth: 450, 
            maxWidth: 600, 
            isResizable: true,
            onRender: (item: CAEndUserAssignmentSummary) => {
                const legalBasesText = item.assignedLegalBases.map(lb => lb.name).join('; ');
                return (
                    <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={legalBasesText}>
                        <span>{legalBasesText}</span>
                    </TooltipHost>
                );
            },
            onRenderHeader: () => (
                <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={'EU sectoral legislative bases'}>{'EU sectoral legislative bases'}</TooltipHost>
            ),
        },
        {
            key: 'actions',
            name: 'Actions',
            fieldName: 'actions',
            minWidth: 100,
            maxWidth: 100,
            onRender: (item: CAEndUserAssignmentSummary) => {
                const menuProps: IContextualMenuProps = {
                    items: _assignmentAction(item),
                    directionalHintFixed: false,
                    className: styles.actionsPanel
                };

                return (
                    <IconButton menuProps={menuProps}
                        onRenderIcon={_onRenderActionsIcon}
                        onRenderMenuIcon={_onRenderActionsMenuIcon}
                        title="Actions"
                        ariaLabel="Actions"
                    />
                );
            },
        },
    ];

    const iconStyleProps: FluentIconsProps = {
        primaryFill: "#0078D4",
        className: styles.filterIcon,
    };

    function onRenderPrefix() {
        return <Delete20Filled aria-label={"Filter"} {...iconStyleProps} />;
    }

    function _assignmentAction(item: CAEndUserAssignmentSummary): IContextualMenuItem[] {
        return [
            {
                key: 'edit',
                text: 'Edit',
                iconProps: { iconName: 'EditSolid12' },
                onClick: () => handleEditClick(item),
            },
            {
                key: 'delete',
                text: 'Delete',
                onRenderIcon: onRenderPrefix,
                onClick: () => handleDeleteClick(item),
            },
        ];
    }

    function _onRenderActionsIcon() {
        return <EiopaFnPIcons iconName={"actions"} iconColor={"#0078D4"} />;
    }

    function _onRenderActionsMenuIcon() {
        return <></>;
    }

    // State hooks for pagination and column management
    const [state, setState] = useState<IPagination>({
        columnsState: assignmentColumns,
        currentPage: 1,
        itemsPerPage: 10,
        totalItems: 0
    });

    function _onColumnClick(event: React.MouseEvent<HTMLElement>, column: IColumn) {
        toggleIsSortedDescendings();
        let isSortedDescending = column.isSortedDescending;

        if (column.isSorted) {
            isSortedDescending = !isSortedDescending;
        }

        let columns = state.columnsState.map(col => {
            col.isSorted = col.key === column.key;
            if (col.isSorted) {
                col.isSortedDescending = isSortedDescending;
            }
            return col;
        });

        const newItems = _copyAndSort(itemsOrg, column.fieldName!, isSortedDescending);
        setItemsOrg(newItems);
        calculatePaginationItems(newItems);
    }

    function _copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
        const key = columnKey as keyof T;
        return items.slice(0).sort((a: T, b: T) => {
            const aValue = String(a[key]);
            const bValue = String(b[key]);
            return isSortedDescending ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
        });
    }

    // Modal event handlers
    const resetIsEdit = (response: any) => {
        if (response === 'close') {
            setIsEdit(false);
            setIsAddNew(false);
            setEditItem(null);
        }
    }

    const resetDeleteDialogHide = (response: any) => {
        if (response === 'close') {
            setDeleteDialogHide(true);
            setDeleteUserEmail('');
            setDeleteItem(null);
        }
    }

    const handleEditClick = (assignment: CAEndUserAssignmentSummary) => {
        setIsEdit(true);
        setIsAddNew(false);
        setEditItem(assignment);
    };

    const handleDeleteClick = (assignment: CAEndUserAssignmentSummary) => {
        setDeleteDialogHide(false);
        setDeleteUserEmail(assignment.userEmail);
        setDeleteItem(assignment);
    };

    const handleAddNewClick = () => {
        setIsEdit(false);
        setIsAddNew(true);
        setEditItem(null);
    };

    // Data fetching functions
    function fetchAndCalculatePages() {
        (async function () {
            try {
                const [assignmentsResult, usersResult, legalBasesResult] = await Promise.all([
                    fetchCAEndUserAssignments(auth.user?.id_token),
                    fetchAvailableCAEndUsers(auth.user?.id_token),
                    fetchActiveLegalBases(auth.user?.id_token)
                ]);

                setItems(assignmentsResult);
                setItemsOrg(assignmentsResult);
                setTotalItems(assignmentsResult.length);
                setAvailableUsers(usersResult);
                setActiveLegalBases(legalBasesResult);
            } catch (error) {
                console.error('Error fetching data:', error);
                displayMessageBar(MessageBarType.error, 'Error loading CA end user assignments.', false);
            }
        })();

        calculatePaginationItems(itemsOrg);
    }

    function calculatePaginationItems(items: CAEndUserAssignmentSummary[]) {
        const startIndex = (state.currentPage - 1) * state.itemsPerPage;
        const endIndex = Math.min(startIndex + state.itemsPerPage, items.length);
        const itemsOnCurrentPage = items.slice(startIndex, endIndex);
        setItems(itemsOnCurrentPage);
    }

    // Fetch data on component mount
    useEffect(() => {
        fetchAndCalculatePages();
    }, []);

    // Pagination
    const totalItemsToPass = totalItems !== undefined ? totalItems : 0;

    function _onPageChange(selectedItem: number) {
        setState({
            ...state,
            currentPage: selectedItem
        });
    }

    useEffect(() => {
        calculatePaginationItems(itemsOrg);
    }, [state.currentPage, state.itemsPerPage, itemsOrg]);

    function _onChangeItemsPerPage(event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) {
        setState({
            ...state,
            itemsPerPage: item.key as number,
            currentPage: 1
        });
    }

    // Selection hook for DetailsList
    const selection = new Selection({
        onSelectionChanged: () => {
            // Update the selected items when the user changes the selection
        },
    });

    // Quick search handler
    const handleItemsProcessed = (processedData: any) => {
        setTotalItems(processedData.length);

        if (processedData.length > 0) {
            calculatePaginationItems(processedData);
            setState({
                ...state,
                currentPage: 1
            });
        } else {
            setItems([]);
        }
    };

    // Handle responses from modal actions
    const handleActionResponse = (response: string) => {
        fetchAndCalculatePages();

        let message;
        let messageBarType;

        switch (response) {
            case "createtrue":
            case "success":
                message = "The CA end user was successfully assigned to the EU sectoral legislative bases.";
                messageBarType = MessageBarType.success;
                break;
            case "edittrue":
                message = "The EU sectoral legislative bases assignment was successfully updated.";
                messageBarType = MessageBarType.success;
                break;
            case "deletetrue":
                message = "The EU sectoral legislative bases assignment was successfully deleted.";
                messageBarType = MessageBarType.success;
                break;
            case "createfalse":
            case "editfalse":
            case "deletefalse":
                message = "An error occurred while processing the request.";
                messageBarType = MessageBarType.error;
                break;
            default:
                if (typeof response === 'string' && response.includes('error')) {
                    message = response;
                    messageBarType = MessageBarType.error;
                } else {
                    message = "Operation completed.";
                    messageBarType = MessageBarType.success;
                }
                break;
        }

        displayMessageBar(messageBarType, message, false);
    };

    function _renderItemColumn(item?: any, index?: number, column?: IColumn) {
        if (item && column) {
            const fieldContent = item[column.fieldName as keyof any] as string;
            return <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={fieldContent}>{fieldContent}</TooltipHost>
        }
    }

    const addTooltipToColumnHeader = (cols: IColumn[]) => {
        return cols.map((column) => ({
            ...column,
            onRenderHeader: () => (
                <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={column.name}>{column.name}</TooltipHost>
            ),
        }))
    }



    return (
        <>
            <StackItem styles={stackStyles}>
                <StackItem className={styles.assignmentHeader}>
                    EU legal basis assignments
                </StackItem>
                
                <div className={styles.headerbar}>
                    <QuickSearch 
                        options={options} 
                        items={items} 
                        itemsOrg={itemsOrg} 
                        onItemsProcessed={handleItemsProcessed} 
                        columns={assignmentColumns} 
                        selection={[]}
                        textFieldStyles={customTextFieldStyles} 
                        dropdownStyles={customDropdownStyles} 
                    />
                    


                    <ActionButton 
                        iconProps={{ iconName: 'Add' }} 
                        onClick={handleAddNewClick} 
                        className={styles.FilterButton}
                    >
                        Add assignment
                    </ActionButton>

                </div>

                <CAEndUserAssignmentDeleteModal 
                    resetDeleteDialogHide={resetDeleteDialogHide} 
                    deleteDialogHide={deleteDialogHide} 
                    deleteUserEmail={deleteUserEmail} 
                    deleteItem={deleteItem} 
                    handleActionResponse={handleActionResponse} 
                />
                
                <CAEndUserAssignmentModal 
                    handleActionResponse={handleActionResponse} 
                    options={assignmentColumns} 
                    isEdit={isEdit} 
                    isAddNew={isAddNew}
                    resetIsEdit={resetIsEdit} 
                    editItem={editItem}
                    availableUsers={availableUsers}
                    activeLegalBases={activeLegalBases}
                />

                {itemsOrg.length === 0 ? (
                    // Don't show anything when there are no assignments at all
                    <Stack styles={stackStylesNoResults}>
                        {strings.noResultsText}
                    </Stack>
                ) : items.length === 0 ? (
                    // Show "No results found" when search returns no results but original data exists
                 <Stack styles={stackStylesNoResults}>
                        {strings.noResultsText}
                    </Stack>
                ) : (
                    // Show normal table when there are results
                    <DetailsList
                        selection={selection}
                        selectionMode={SelectionMode.none}
                        selectionPreservedOnEmptyClick={true}
                        layoutMode={1}
                        columns={addTooltipToColumnHeader(assignmentColumns)}
                        items={items}
                        onRenderItemColumn={_renderItemColumn}
                    />
                )}

            </StackItem>
            
            {itemsOrg.length > 0 && items.length > 0 && (
                <StackItem>
                    <Pagination
                        currentPage={state.currentPage}
                        onPageChange={_onPageChange}
                        itemsPerPage={state.itemsPerPage}
                        onChangeItemsPerPage={_onChangeItemsPerPage}
                        totalItems={totalItemsToPass}
                    />
                </StackItem>
            )}
        </>
    );
}

export default CAEndUserAssignment;
